import { GutXAxisOrgan, OrganLookupCode } from '@repo/types';
import { Icon } from '@repo/ui';
import c from 'classnames';
import { useState } from 'react';
import { organAnchorId } from 'views/Results/utils/organs';
import { useOrganSelect } from './hooks';
import styles from './OrganSelector.module.css';
import OrganSelectorRow from './OrganSelectorRow';

type OrganSelectorProps = {
  organs: GutXAxisOrgan[];
  /** Anchor of the section currently in view, so the list marks where you are. */
  activeId: string;
  /** `kit.age_at_sampling`, in days. */
  age: number;
  pregnant: boolean;
  sex: string;
  /** Every code the map draws, so the badges crop the same gut. */
  drawnCodes?: ReadonlySet<OrganLookupCode>;
};

/**
 * Jumps between organ sections on mobile, where the body map is hidden and
 * there is otherwise nothing to navigate by.
 */
export default function OrganSelector({
  organs,
  activeId,
  age,
  pregnant,
  sex,
  drawnCodes,
}: OrganSelectorProps) {
  const [isOpen, setIsOpen] = useState(false);
  const selectOrgan = useOrganSelect();

  const selected =
    organs.find(organ => organAnchorId(organ.lookup_code) === activeId) ??
    organs[0];

  if (!selected) return null;

  const bodyProps = { age, pregnant, sex, drawnCodes };

  function handleSelect(organ: GutXAxisOrgan) {
    setIsOpen(false);
    selectOrgan(organ);
  }

  // Focus moving between the trigger and an option is not leaving the selector.
  function handleBlur(event: React.FocusEvent<HTMLDivElement>) {
    if (!event.currentTarget.contains(event.relatedTarget)) setIsOpen(false);
  }

  return (
    <div className={c(styles.container, 'hide-on-desktop')} onBlur={handleBlur}>
      <button
        type='button'
        aria-expanded={isOpen}
        className={c(styles.trigger, isOpen && styles.opened)}
        onClick={() => setIsOpen(open => !open)}
      >
        <OrganSelectorRow organ={selected} {...bodyProps} />
        <Icon name='chevronDown' className={styles.chevron} />
      </button>
      {isOpen && (
        <div className={styles.dropdown}>
          {organs.map(organ => (
            <button
              key={organ.id}
              type='button'
              className={c(
                styles.option,
                organAnchorId(organ.lookup_code) === activeId && styles.active,
              )}
              onClick={() => handleSelect(organ)}
            >
              <OrganSelectorRow organ={organ} {...bodyProps} />
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
